'use client'

/**
 * VideoDownloadButton — the "download this video" overlay for legacy-premium
 * members, dropped into the top-right corner of every Vimeo player surface
 * (course cover player, body videoBlocks, scene video tabs, flogger video
 * tab).
 *
 * It renders NOTHING unless /api/video-downloads/me says the visitor is
 * entitled (see useVideoDownloadEntitlement — one request per page load, no
 * matter how many players are on the page), so for everyone else the player
 * is byte-for-byte what it was.
 *
 * Styled exactly like the course player's iPhone fullscreen button (a 36px
 * rounded bg-black/55 square) so the two read as one set of player chrome;
 * `offsetTop` drops it a row so it stacks under that button when both show.
 *
 * Click flow (all state is local to this button — nothing is fetched on
 * mount beyond the shared entitlement):
 *   idle      → POST /api/video-downloads { vimeoId }
 *               201 pending   → "queued": poll the ledger every 5 s until the
 *                               job is terminal, bounded to ~10 min, then
 *                               "stalled" (the email + Account → Downloads
 *                               take over from there)
 *               200 reused    → the member already has this video in flight
 *                               or cached: land directly on queued / ready
 *               429           → cap message from the route, shown briefly
 *               403 / 404     → "not available", shown briefly
 *   ready     → GET /api/video-downloads/[id]/link → navigate to the signed
 *               URL (with `download=` so the browser saves rather than plays)
 *   failed    → click retries (a failed job isn't reused by POST, so this
 *               queues a fresh transcode)
 *
 * The small label chip next to the icon appears for a few seconds after
 * every state change and on hover/focus, so the icon alone never has to
 * carry the message.
 *
 * The route-client helpers at the top are exported for the account
 * Downloads tab so both surfaces parse the same payloads the same way.
 */

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Check, Clock, Download, Loader2 } from 'lucide-react'
import { useVideoDownloadEntitlement } from '@/hooks/use-video-download-entitlement'

// ---------------------------------------------------------------------------
// Route client (shared with downloads-tab.tsx)
// ---------------------------------------------------------------------------

export type VideoDownloadJobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'expired'

/** `publicJob()` from /api/video-downloads — the member-safe row subset. */
export interface PublicVideoDownloadJob {
  id: string
  vimeo_id: string
  content_type: string | null
  content_slug: string | null
  title: string | null
  download_code: string
  status: VideoDownloadJobStatus
  rendition: string | null
  size_bytes: number | null
  requested_at: string
  completed_at: string | null
  expires_at: string | null
  download_count: number
  last_error: string | null
  /** Live render progress 0–100, only set while `processing` (else null). */
  progress_percent: number | null
  /** Estimated seconds remaining, only set while `processing` (else null). */
  eta_seconds: number | null
}

export type RouteFailure = { ok: false; status: number; error: string }

const GENERIC_ERROR = 'Something went wrong — please try again.'

async function readJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const j = (await res.json()) as unknown
    return j && typeof j === 'object' ? (j as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

function failure(res: Response, payload: Record<string, unknown>, fallback: string): RouteFailure {
  return { ok: false, status: res.status, error: typeof payload.error === 'string' ? payload.error : fallback }
}

function networkFailure(e: unknown): RouteFailure {
  return { ok: false, status: 0, error: e instanceof Error && e.message ? e.message : 'Network error' }
}

/** POST /api/video-downloads — queue (or reuse) a watermarked download. */
export async function requestVideoDownload(
  vimeoId: string,
): Promise<{ ok: true; job: PublicVideoDownloadJob; reused: boolean } | RouteFailure> {
  try {
    const res = await fetch('/api/video-downloads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ vimeoId }),
    })
    const payload = await readJson(res)
    if (!res.ok || !payload.job) return failure(res, payload, GENERIC_ERROR)
    return { ok: true, job: payload.job as PublicVideoDownloadJob, reused: payload.reused === true }
  } catch (e) {
    return networkFailure(e)
  }
}

/** GET /api/video-downloads — the caller's ledger, newest first. */
export async function fetchVideoDownloadLedger(): Promise<
  { ok: true; downloads: PublicVideoDownloadJob[] } | RouteFailure
> {
  try {
    const res = await fetch('/api/video-downloads', { cache: 'no-store', credentials: 'same-origin' })
    const payload = await readJson(res)
    if (!res.ok) return failure(res, payload, GENERIC_ERROR)
    return { ok: true, downloads: Array.isArray(payload.downloads) ? (payload.downloads as PublicVideoDownloadJob[]) : [] }
  } catch (e) {
    return networkFailure(e)
  }
}

/** GET /api/video-downloads/[id]/link — a short-lived signed URL. */
export async function fetchVideoDownloadLink(jobId: string): Promise<{ ok: true; url: string } | RouteFailure> {
  try {
    const res = await fetch(`/api/video-downloads/${encodeURIComponent(jobId)}/link`, {
      cache: 'no-store',
      credentials: 'same-origin',
    })
    const payload = await readJson(res)
    if (!res.ok || typeof payload.url !== 'string') return failure(res, payload, GENERIC_ERROR)
    return { ok: true, url: payload.url }
  } catch (e) {
    return networkFailure(e)
  }
}

/** A completed job whose file is past `expires_at` is effectively expired
 *  even before the reaper flips the row. */
export function isVideoDownloadExpired(job: PublicVideoDownloadJob, now = Date.now()): boolean {
  if (job.status === 'expired') return true
  return job.status === 'completed' && !!job.expires_at && new Date(job.expires_at).getTime() <= now
}

/** Still in flight, or cached and downloadable — the states POST would reuse. */
export function isVideoDownloadLive(job: PublicVideoDownloadJob, now = Date.now()): boolean {
  if (job.status === 'pending' || job.status === 'processing') return true
  return job.status === 'completed' && !isVideoDownloadExpired(job, now)
}

/** `my-tutorial-title-720p.mp4` — ASCII-only so every browser keeps it. */
export function videoDownloadFilename(title: string | null | undefined, vimeoId: string): string {
  const base = (title || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
  return `${base || `theduchy-${vimeoId}`}-720p.mp4`
}

/**
 * Supabase Storage honours a `download=<filename>` query param on signed
 * object URLs (it's what the SDK's `{ download }` option appends): the
 * response gains `Content-Disposition: attachment`, so navigating to it
 * SAVES the MP4 instead of replacing the page with a bare video player.
 */
export function asAttachmentUrl(url: string, filename: string): string {
  try {
    const u = new URL(url, window.location.origin)
    if (!u.searchParams.has('download')) u.searchParams.set('download', filename)
    return u.toString()
  } catch {
    return url
  }
}

/** Mint a link for a finished job and hand it to the browser. */
export async function openVideoDownload(job: PublicVideoDownloadJob): Promise<{ ok: true } | RouteFailure> {
  const link = await fetchVideoDownloadLink(job.id)
  if (!link.ok) return link
  window.location.assign(asAttachmentUrl(link.url, videoDownloadFilename(job.title, job.vimeo_id)))
  return { ok: true }
}

/** Seconds → "m:ss" for the queued-state countdown (locale-neutral). */
function mmss(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 5_000
/** Give up polling after this long — the ready email and the account tab
 *  cover a slow transcode; the button shouldn't hammer the ledger forever. */
const POLL_BUDGET_MS = 10 * 60_000
/** How long a 429 / 403 / 404 notice stays before the button reverts to idle. */
const NOTICE_MS = 6_000
/** How long the label chip stays visible after a state change. */
const LABEL_MS = 6_000

type Phase =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | { kind: 'queued'; job: PublicVideoDownloadJob }
  | { kind: 'ready'; job: PublicVideoDownloadJob }
  | { kind: 'opening'; job: PublicVideoDownloadJob }
  | { kind: 'stalled'; job: PublicVideoDownloadJob }
  | { kind: 'failed'; message: string }
  | { kind: 'notice'; message: string }

const LABELS = {
  requesting: 'Requesting…',
  queued: "Preparing your download… we'll email you when it's ready",
  ready: 'Download ready',
  opening: 'Opening your download…',
  stalled: 'Still preparing — check Account → Downloads',
  notAvailable: 'Not available for download',
  expired: 'This download has expired — request it again',
} as const

interface VideoDownloadButtonProps {
  vimeoId: string
  /** The same text the surrounding player puts in the iframe's `title`. */
  title: string
  className?: string
  /** Drop to `top-12` so the button stacks under the course player's
   *  fullscreen button when that one is showing. */
  offsetTop?: boolean
}

export function VideoDownloadButton({ vimeoId, title, className = '', offsetTop = false }: VideoDownloadButtonProps) {
  const { enabled } = useVideoDownloadEntitlement()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [labelVisible, setLabelVisible] = useState(false)
  // Live progress for the queued state, kept OUT of `phase` so the 5 s poll
  // updating it every tick doesn't re-key (and reset the budget of) the poll
  // effect below. Populated from the polled row; reset when we leave queued.
  const [progress, setProgress] = useState<{ pct: number | null; eta: number | null }>({ pct: null, eta: null })

  // Every user-facing state change also re-shows the label chip.
  const transition = useCallback((next: Phase) => {
    setPhase(next)
    setLabelVisible(true)
  }, [])

  // Hide the label chip a few seconds after the latest state change. `phase`
  // is a deliberate extra dependency so a change while the chip is already
  // showing restarts the timer instead of cutting the new message short.
  useEffect(() => {
    if (!labelVisible) return
    const id = window.setTimeout(() => setLabelVisible(false), LABEL_MS)
    return () => window.clearTimeout(id)
  }, [labelVisible, phase])

  // Transient notices (cap reached / not available) revert to idle.
  useEffect(() => {
    if (phase.kind !== 'notice') return
    const id = window.setTimeout(() => setPhase({ kind: 'idle' }), NOTICE_MS)
    return () => window.clearTimeout(id)
  }, [phase])

  // Poll the ledger while the job is in flight. The effect is keyed on the
  // `queued` phase object, so the budget starts when we enter it and resets
  // only on a fresh request (a click while "stalled" re-POSTs, which reuses
  // the same in-flight job and starts a new 10-minute window).
  useEffect(() => {
    if (phase.kind !== 'queued') return
    const job = phase.job
    const startedAt = Date.now()
    let cancelled = false
    const tick = async () => {
      if (Date.now() - startedAt > POLL_BUDGET_MS) {
        transition({ kind: 'stalled', job })
        return
      }
      const res = await fetchVideoDownloadLedger()
      if (cancelled || !res.ok) return
      const fresh = res.downloads.find((d) => d.id === job.id)
      if (!fresh) return
      if (fresh.status === 'completed') transition({ kind: 'ready', job: fresh })
      else if (fresh.status === 'failed') transition({ kind: 'failed', message: fresh.last_error || 'Download failed' })
      else if (fresh.status === 'expired') transition({ kind: 'idle' })
      else setProgress({ pct: fresh.progress_percent, eta: fresh.eta_seconds }) // still preparing — refresh the bar/ETA
    }
    const id = window.setInterval(() => void tick(), POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [phase, transition])

  // Hooks above run for everyone; the early return keeps the DOM untouched
  // for members who can't download (and for anonymous visitors).
  if (!enabled) return null

  const request = async () => {
    setProgress({ pct: null, eta: null }) // fresh start — clear any prior job's bar
    transition({ kind: 'requesting' })
    const res = await requestVideoDownload(vimeoId)
    if (!res.ok) {
      if (res.status === 429) transition({ kind: 'notice', message: res.error })
      else if (res.status === 403 || res.status === 404) transition({ kind: 'notice', message: LABELS.notAvailable })
      else transition({ kind: 'failed', message: res.error })
      return
    }
    const { job } = res
    if (job.status === 'completed' && !isVideoDownloadExpired(job)) transition({ kind: 'ready', job })
    else if (job.status === 'pending' || job.status === 'processing') transition({ kind: 'queued', job })
    else if (job.status === 'failed') transition({ kind: 'failed', message: job.last_error || 'Download failed' })
    else transition({ kind: 'idle' })
  }

  const open = async (job: PublicVideoDownloadJob) => {
    transition({ kind: 'opening', job })
    const res = await openVideoDownload(job)
    if (res.ok) {
      transition({ kind: 'ready', job })
      return
    }
    // 410: the cached file aged out since we last looked → back to idle so
    // the next click requests it again. 409: the worker hasn't finished
    // after all → resume polling.
    if (res.status === 410) transition({ kind: 'notice', message: LABELS.expired })
    else if (res.status === 409) transition({ kind: 'queued', job })
    else transition({ kind: 'failed', message: res.error })
  }

  const onClick = () => {
    switch (phase.kind) {
      case 'idle':
      case 'notice':
      case 'failed':
      case 'stalled':
        void request()
        return
      case 'ready':
        void open(phase.job)
        return
      default:
        // requesting / queued / opening — the click is a no-op; the label
        // chip already says what's happening.
        return
    }
  }

  const busy = phase.kind === 'requesting' || phase.kind === 'queued' || phase.kind === 'opening'
  const baseAria = `Download “${title}” (720p)`
  let label: string | null = null
  let aria = baseAria
  let Icon = Download
  switch (phase.kind) {
    case 'requesting':
      label = LABELS.requesting
      aria = `${baseAria} — ${label}`
      Icon = Loader2
      break
    case 'queued':
      // Show live progress once the render is actually encoding; until then
      // (queued / pre-encode / cold start) keep the plain "preparing" copy.
      label =
        progress.pct != null
          ? `Preparing… ${progress.pct}%${progress.eta != null ? ` · ${mmss(progress.eta)} left` : ''}`
          : LABELS.queued
      aria = `${baseAria} — ${label}`
      Icon = Loader2
      break
    case 'opening':
      label = LABELS.opening
      aria = `${baseAria} — ${label}`
      Icon = Loader2
      break
    case 'ready':
      label = LABELS.ready
      aria = `${baseAria} — ${label}`
      Icon = Check
      break
    case 'stalled':
      label = LABELS.stalled
      aria = `${baseAria} — ${label}`
      Icon = Clock
      break
    case 'failed':
      label = phase.message
      aria = `${baseAria} — failed: ${label}`
      Icon = AlertTriangle
      break
    case 'notice':
      label = phase.message
      aria = `${baseAria} — ${label}`
      break
    default:
      break
  }

  return (
    <div
      className={`group absolute right-2 z-10 flex items-center gap-2 ${offsetTop ? 'top-12' : 'top-2'} ${className}`}
    >
      {label && (
        <div
          aria-hidden
          className={`pointer-events-none max-w-[min(70vw,320px)] rounded bg-black/55 px-2 py-1 text-xs text-white backdrop-blur-sm transition-opacity ${
            labelVisible || phase.kind === 'queued'
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
          }`}
        >
          <span className="block truncate">{label}</span>
          {phase.kind === 'queued' && progress.pct != null && (
            <span className="mt-1 block h-1 w-full overflow-hidden rounded-full bg-white/25">
              <span
                className="block h-full rounded-full bg-white transition-all duration-500"
                style={{ width: `${Math.min(100, Math.max(0, progress.pct))}%` }}
              />
            </span>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onClick}
        aria-label={aria}
        aria-busy={busy || undefined}
        title={label ?? baseAria}
        className={`flex h-9 w-9 items-center justify-center rounded bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
          busy ? 'cursor-progress' : ''
        }`}
      >
        <Icon className={`h-5 w-5 ${busy ? 'animate-spin' : ''}`} aria-hidden />
      </button>
    </div>
  )
}
